import { createContext, useContext } from 'react';
import { createClient, type SupabaseClient, type Session, type User } from '@supabase/supabase-js';
import { getConfig } from './config';

export interface AuthContextValue {
  readonly user: User | null;
  readonly session: Session | null;
  readonly loading: boolean;
  /**
   * Sign in with email + password. The admin portal additionally enforces
   * role=superadmin once the session is established (see AuthProvider).
   */
  readonly signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  readonly signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

let supabaseClientPromise: Promise<SupabaseClient> | null = null;

export function getSupabaseClient(): Promise<SupabaseClient> {
  if (!supabaseClientPromise) {
    supabaseClientPromise = getConfig().then(({ supabaseUrl, supabaseAnonKey }) =>
      createClient(supabaseUrl, supabaseAnonKey, {
        auth: { persistSession: true, autoRefreshToken: true },
      }),
    );
  }
  return supabaseClientPromise;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
